import { FolderCopy as FolderCopyIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { permission } from '@nohara-dxdev/oidc-core';
import { useNotifications } from '@nohara-dxdev/react-core';
import { useCanAccess, useOidc, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getBuildingsQueryKey } from '@/features/buildings/api/get-buildings';
import { api } from '@/lib/api-client';
import { BuildingStructure, ProjectEntity, RoomEntity } from '@/types/api';

import { useCopyItemsFromRoom } from '../api/copy-items-from-room';
import { useCopyRoomFromProject } from '../api/copy-room-from-project';
import { useCopyableItemCount } from '../api/get-copyable-item-count';
import { getItemsQueryKey } from '../api/get-items';
import { useJobStatus } from '../api/get-job-status';

import type { ApiResponse } from '@nohara-dxdev/oidc-core';

/**
 * Where the copied data lands.
 *   - mode: 'room'  → copy items into an existing room (MT1-1393).
 *   - mode: 'floor' → create a new room on the floor and copy items into
 *     it (MT1-1397). After success, the parent gets the new room id via
 *     `onRoomCreated` so it can auto-select the new room in the cascade.
 */
export type CopyItemsFromRoomTarget =
  | { mode: 'room'; buildingId: string; roomId: string }
  | { mode: 'floor'; buildingId: string; floorId: string };

interface CopyItemsFromRoomDialogProps {
  target: CopyItemsFromRoomTarget;
  disabled?: boolean;
  /** Called after a successful floor-mode copy with the id of the newly created room. */
  onRoomCreated?: (newRoomId: string) => void;
}

type RoomOption = RoomEntity & { _buildingName: string; _floorName: string };

const fetchBuildingsForProject = async (
  orgId: string,
  projectId: string,
): Promise<BuildingStructure[]> => {
  const { data } = await api.get<unknown, ApiResponse<BuildingStructure[]>>(
    `/orgs/${orgId}/projects/${projectId}/buildings`,
  );
  return data;
};

const useBuildingsForProject = (orgId: string, projectId: string | undefined) =>
  useQuery({
    queryKey: ['org', orgId, 'project', projectId, 'buildings'],
    queryFn: () => fetchBuildingsForProject(orgId, projectId ?? ''),
    enabled: !!projectId,
  });

const fetchAllAssignedProjects = async (
  orgId: string,
  userId: string,
): Promise<ProjectEntity[]> => {
  const { data } = await api.get<unknown, ApiResponse<ProjectEntity[]>>(
    `/orgs/${orgId}/users/${userId}/projects`,
  );
  return data;
};

const useAssignedProjects = (orgId: string, userId: string | undefined) =>
  useQuery({
    queryKey: ['projects', 'org', orgId, 'user', userId],
    queryFn: () => fetchAllAssignedProjects(orgId, userId ?? ''),
    enabled: !!userId,
  });

// App Admin / root path: the user-assigned endpoint returns nothing because
// admins aren't members of any single project. Fetch the org-wide project
// list instead so they can still pick a source. Endpoint is paginated; the
// dialog only needs the first page (orgs typically hold < 100 projects).
const fetchAllOrgProjects = async (orgId: string): Promise<ProjectEntity[]> => {
  const { data } = await api.get<unknown, ApiResponse<{ data: ProjectEntity[]; cursor?: string }>>(
    `/orgs/${orgId}/projects`,
  );
  return data.data;
};

const useAllOrgProjects = (orgId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['projects', 'org', orgId, 'all'],
    queryFn: () => fetchAllOrgProjects(orgId),
    enabled,
  });

export const CopyItemsFromRoomDialog = ({
  target,
  disabled,
  // onRoomCreated is currently dead in the async redesign — the new room's id
  // isn't known until the worker runs. The prop stays on the interface so
  // restoring auto-select via job-status polling doesn't require a callsite
  // change, and the room.tsx parent's hookup is forward-compatible.
  onRoomCreated: _onRoomCreated,
}: CopyItemsFromRoomDialogProps) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const { orgId, projectId: currentProjectId } = useScopeIds('org', 'project');
  const { userId } = useOidc();

  const [open, setOpen] = useState(false);
  const [sourceProject, setSourceProject] = useState<ProjectEntity | null>(null);
  const [sourceRoom, setSourceRoom] = useState<RoomOption | null>(null);

  // App Admin (root) detection: regular designers have project-scoped role
  // grants so this `useCanAccess` at the org context returns false; App Admin
  // has `permission.all.manage` app-wide, which covers `project.read` at the
  // org scope, so it returns true. The dialog then fetches the org-wide
  // project list because the user-assigned endpoint would return [] for
  // admins (they aren't a member of any single project).
  const { canAccess: isOrgWideReader } = useCanAccess({
    requireAll: [permission.project.read],
    context: { orgId },
  });

  const assignedProjectsQuery = useAssignedProjects(orgId, isOrgWideReader ? undefined : userId);
  const allOrgProjectsQuery = useAllOrgProjects(orgId, isOrgWideReader);
  const projectsQuery = isOrgWideReader ? allOrgProjectsQuery : assignedProjectsQuery;
  const buildingsQuery = useBuildingsForProject(orgId, sourceProject?.id);

  // Sanity-check the user's selection before they commit — "X items will be
  // imported" / "no items in this room". Cheap OpenSearch count keyed on the
  // same filter the worker uses, so the number is the upper bound on what
  // the copy will attempt (a few may still skip on target-side validation).
  // buildingId comes from the flattened RoomOption — the endpoint lives at
  // /buildings/{buildingId}/rooms/{roomId}/... because rooms aren't directly
  // addressable under a project.
  const copyableCountQuery = useCopyableItemCount({
    orgId,
    sourceProjectId: sourceProject?.id,
    sourceBuildingId: sourceRoom?.buildingId,
    sourceRoomId: sourceRoom?.id,
  });

  const projectOptions = useMemo(
    () => (projectsQuery.data ?? []).filter((p) => p.id !== currentProjectId),
    [projectsQuery.data, currentProjectId],
  );

  // Flatten the nested building → floor → room shape into a single option
  // list, tagging each room with its building and floor names for display
  // and grouping. Group label keeps the cascade visible without needing a
  // multi-step picker.
  const roomOptions = useMemo<RoomOption[]>(() => {
    const buildings = buildingsQuery.data ?? [];
    const rooms: RoomOption[] = [];
    for (const building of buildings) {
      for (const floor of building.floors) {
        for (const room of floor.rooms) {
          rooms.push({ ...room, _buildingName: building.name, _floorName: floor.name });
        }
      }
    }
    return rooms;
  }, [buildingsQuery.data]);

  const resetState = () => {
    setSourceProject(null);
    setSourceRoom(null);
  };

  // MT1-1393 redesign: the POST endpoint enqueues a job and returns the Job
  // entity. We stash the jobId in local state and poll GET /jobs/{jobId} until
  // it reaches a terminal status. The button itself flips to a "Copying…"
  // spinner while the worker runs and the user sees a real completion toast
  // (with copied/skipped counts) when the worker finishes. Failures surface
  // the error string from the job record.
  const queryClient = useQueryClient();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const jobStatusQuery = useJobStatus({ jobId: activeJobId });

  // Single source of truth for "is there an in-flight copy". Used by the
  // button label + disabled state and by the dialog close guard so we don't
  // let the user fire a second copy until the first lands.
  const inFlight =
    activeJobId !== null &&
    jobStatusQuery.data?.status !== 'completed' &&
    jobStatusQuery.data?.status !== 'failed';

  useEffect(() => {
    const job = jobStatusQuery.data;
    if (!activeJobId || !job) return;
    if (job.status === 'completed') {
      // Items + zones invalidate when the worker writes them; trigger again
      // here as a backstop so the items grid refreshes once the user sees
      // the completion toast.
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, currentProjectId) });
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      if (target.mode === 'floor') {
        // The new room appears in the building tree on completion; surface
        // it now so the cascade picker shows it on the next render.
        void queryClient.invalidateQueries({
          queryKey: getBuildingsQueryKey(orgId, currentProjectId),
        });
      }
      const copied = job.response?.copied ?? 0;
      const skipped = job.response?.skipped ?? 0;
      const skipReasons = job.response?.skipReasons ?? [];
      // Show the first reason inline when items were skipped — the user can
      // see what's failing (e.g. "Item template not found") without grepping
      // CloudWatch. We cap server-side at 5 and surface the first; identical
      // reasons across all skips are common (one missing template → every
      // item skipped) so the first reason is usually enough to act on.
      const firstReason = skipReasons[0]?.reason;
      // Two full-sentence keys rather than concatenating a suffix — sentence
      // structure and punctuation differ across languages, so translators own
      // the whole string.
      const message =
        skipped > 0 && firstReason
          ? t('items.copy.completedWithReason', {
              copied,
              skipped,
              reason: firstReason,
              defaultValue:
                'Copy complete — {{copied}} items copied, {{skipped}} skipped. Reason: {{reason}}',
            })
          : t('items.copy.completed', {
              copied,
              skipped,
              defaultValue: 'Copy complete — {{copied}} items copied, {{skipped}} skipped.',
            });
      notifications.success(message);
      setActiveJobId(null);
    } else if (job.status === 'failed') {
      notifications.error(
        t('items.copy.failed', {
          error: job.error ?? 'unknown error',
          defaultValue: 'Copy failed: {{error}}',
        }),
      );
      setActiveJobId(null);
    }
  }, [
    activeJobId,
    jobStatusQuery.data,
    queryClient,
    orgId,
    currentProjectId,
    target.mode,
    notifications,
    t,
  ]);

  const copyItemsMutation = useCopyItemsFromRoom({
    mutationConfig: {
      onSuccess: (job) => {
        // Hand off to the polling effect. Close the dialog immediately —
        // progress shows in the button label.
        setActiveJobId(job.id);
        setOpen(false);
        resetState();
      },
      onError: () => {
        notifications.error(
          t('items.copyFromRoom.error', { defaultValue: 'Failed to enqueue room copy.' }),
        );
      },
    },
  });

  const copyRoomMutation = useCopyRoomFromProject({
    mutationConfig: {
      onSuccess: (job) => {
        setActiveJobId(job.id);
        setOpen(false);
        resetState();
      },
      onError: () => {
        notifications.error(
          t('items.copyRoomFromProject.error', {
            defaultValue: 'Failed to enqueue floor-target room copy.',
          }),
        );
      },
    },
  });

  const isPending = copyItemsMutation.isPending || copyRoomMutation.isPending;

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (isPending) return;
    setOpen(false);
  };

  const handleConfirm = () => {
    if (!sourceProject || !sourceRoom) return;
    if (target.mode === 'room') {
      copyItemsMutation.mutate({
        buildingId: target.buildingId,
        roomId: target.roomId,
        dto: { sourceProjectId: sourceProject.id, sourceRoomId: sourceRoom.id },
      });
    } else {
      copyRoomMutation.mutate({
        buildingId: target.buildingId,
        floorId: target.floorId,
        dto: { sourceProjectId: sourceProject.id, sourceRoomId: sourceRoom.id },
      });
    }
  };

  const canConfirm = !!sourceProject && !!sourceRoom && !isPending;

  // Short button per Kevin's UX call — "Import Items" fits the rail
  // without truncating in either mode. The dialog title still carries
  // the descriptive language so the user knows whether they're pulling
  // items into this room or creating a new room on this floor. While
  // a copy is in flight the button flips to "Importing…" so the user
  // has a real progress signal and the button stays disabled to prevent
  // double-fire.
  const idleButtonLabel = t('items.copyFromRoom.button', 'Import Items');
  const buttonLabel = inFlight ? t('items.copy.inProgress', 'Importing…') : idleButtonLabel;
  const dialogTitle =
    target.mode === 'room'
      ? t('items.copyFromRoom.title', 'Import items from another project room')
      : t('items.copyRoomFromProject.title', 'Import a room (and its items) from another project');
  const dialogDescription =
    target.mode === 'room'
      ? t(
          'items.copyFromRoom.description',
          'Choose a room in another project you have access to. All item instances in that room will be copied into the current room.',
        )
      : t(
          'items.copyRoomFromProject.description',
          'Choose a room in another project you have access to. A new room will be created on this floor with the source room’s name, and all items will be copied into it.',
        );

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FolderCopyIcon />}
        onClick={handleOpen}
        disabled={(disabled ?? false) || inFlight}
      >
        {buttonLabel}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {dialogDescription}
            </Typography>
            <Autocomplete
              options={projectOptions}
              loading={projectsQuery.isLoading}
              value={sourceProject}
              onChange={(_, value) => {
                setSourceProject(value);
                setSourceRoom(null);
              }}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('items.copyFromRoom.projectLabel', 'Source project')}
                  size="small"
                />
              )}
            />
            <Autocomplete
              options={roomOptions}
              loading={buildingsQuery.isFetching}
              value={sourceRoom}
              onChange={(_, value) => {
                setSourceRoom(value);
              }}
              groupBy={(opt) => `${opt._buildingName} / ${opt._floorName}`}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              disabled={!sourceProject}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('items.copyFromRoom.roomLabel', 'Source room')}
                  size="small"
                />
              )}
            />
            {sourceRoom &&
              (copyableCountQuery.isFetching ? (
                <Typography variant="body2" color="text.secondary">
                  {t('items.copyFromRoom.countLoading', 'Counting items in this room…')}
                </Typography>
              ) : copyableCountQuery.data ? (
                <Typography variant="body2" color="text.secondary">
                  {copyableCountQuery.data.count === 0
                    ? t(
                        'items.copyFromRoom.countEmpty',
                        'No items in this room — nothing will be imported.',
                      )
                    : t('items.copyFromRoom.countSummary', {
                        defaultValue:
                          'About to import up to {{count}} item from this room.\nA few may be skipped if the target project is missing matching settings or templates.',
                        defaultValue_other:
                          'About to import up to {{count}} items from this room.\nA few may be skipped if the target project is missing matching settings or templates.',
                        count: copyableCountQuery.data.count,
                      })}
                </Typography>
              ) : null)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={!canConfirm}>
            {t('items.copyFromRoom.confirm', 'Copy')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
